"use client";

import React, { useEffect, useState } from "react";
import { PagePayoutSchema } from "@/sanity/pages/PagePayoutTemplate";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";

import laurels from "@/public/payout/laurels.svg";
import ipad from "@/public/global/ipad.png";
import { PageAssetsSchema } from "@/sanity/pages/PageAssetsTemplate";
import { motion } from "framer-motion";
import ChipSerif from "@/components/global/ui/ChipSerif";
import bulletIcon from "@/public/assets/bulletIcon.svg";

export default function PlatformSection({
  data,
  className,
}: {
  data: PageAssetsSchema;
  className?: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const isBrazil = data?.settings?.slug?.current?.endsWith("/br");

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <section className={cn("relative overflow-clip", className)}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-full border-l border-l-[#E5E5E5] hidden lg:block",
              index >= 2 && index <= 4
                ? "border-l-white"
                : "border-l-[#E5E5E5]",
            )}
          />
        ))}
      </div>

      <div className="container px-[3rem] lg:px-0 flex flex-col items-start lg:items-center py-[8rem] lg:py-[8rem] relative">
        {data?.platformChip && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <ChipSerif
              text={data.platformChip}
              className="text-black/70 mb-0"
            />
          </motion.div>
        )}

        <motion.div
          className="richtext richtext-start lg:richtext-center w-full prose-headings:w-full
          prose-headings:text-[#103037] prose-headings:tracking-normal sm:prose-headings:tracking-[-0.03em] [&_span]:text-[#103037] [&_span]:font-instrument-sans [&_span]:font-semibold lg:[&_span]:font-bold lg:[&_span]:text-[#6DA991] lg:[&_span]:font-gestura
          prose-p:text-black/70 prose-p:text-[2.25rem] lg:prose-p:text-[2rem] prose-p:w-[min(46.5rem,95%)] prose-strong:text-black [text-wrap-style:pretty]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {data?.platformText && (
            <PortableText
              value={data?.platformText}
              components={richTextComponent}
            />
          )}
        </motion.div>

        {data.platformList && (
          <div className="w-[min(151rem,100%)] mt-[4rem] lg:mt-[6rem] flex flex-col gap-[2rem] lg:gap-[6rem]">
            {data.platformList.map((item, index) => (
              <>
                {item.enable && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{
                      duration: 0.3,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                    key={"platform" + index}
                    className="bg-white rounded-[2rem] border border-[#E5E5E5] p-[3rem] flex flex-col lg:flex-row items-center justify-between gap-[3rem]"
                  >
                    <div className="lg:p-[4rem] flex flex-col gap-[3rem] lg:gap-[5rem] w-full">
                      <div className="flex items-center gap-[1.5rem]">
                        <Image
                          src={item.logo}
                          alt={item.name + " platform logo"}
                          className="!w-[5.5rem] shrink-0"
                        />
                        <span className="text-[3rem] lg:text-[4rem] text-[#103037] font-medium tracking-[-0.09rem] lg:tracking-[-0.12rem]">
                          {item.name}
                        </span>
                      </div>

                      <div className="flex flex-col gap-[2rem] lg:gap-[3.5rem]">
                        {item.bullets.map((bullet, indexB) => (
                          <div
                            key={"plat" + index + "bull" + indexB}
                            className="flex items-center gap-[1.5rem]"
                          >
                            <Image
                              src={bulletIcon}
                              alt={"bullet"}
                              className="!w-[3rem] shrink-0"
                            />
                            <span className="text-[2.25rem] text-[#103037] font-normal">
                              {bullet}
                            </span>
                          </div>
                        ))}
                      </div>

                      <ChipSerif text={item.annotation} className="mb-0" />
                    </div>

                    <Image
                      src={item.image}
                      alt={"image"}
                      className="!w-full lg:!w-[67.875rem] shrink-0"
                    />
                  </motion.div>
                )}
              </>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
